import type {
  LockerverseCatalog,
  LockerverseCheckoutClient,
  LockerverseCheckoutEstimate,
  LockerverseCheckoutItem,
  LockerversePaymentSubmission,
  LockerversePreparedCheckout,
  LockerverseShipping,
} from "@lockerverse/sdk/checkout";
import type {
  Stripe,
  StripeAddressElementChangeEvent,
  StripeElements,
} from "@stripe/stripe-js";
import type { CheckoutFieldValues } from "./checkout-fields.tsx";
import {
  buildCustomFieldAnswers,
  getCheckoutCustomFields,
} from "./custom-fields.tsx";
import { notifyHostCallback } from "./host-callback.ts";
import {
  hasMaterialQuoteChange,
  type PaymentAttemptOutcome,
  submitPaymentAttempt,
} from "./payment-attempt.ts";
import { buildQuoteRequest, getTipCents } from "./payment-quote.ts";
import { normalizePhone } from "./payment-validation.ts";
import type { LockerversePaymentProps } from "./types.ts";

export type CheckoutAddresses = Record<
  "billing" | "shipping",
  StripeAddressElementChangeEvent | null
>;

function getCompleteAddress(
  address: StripeAddressElementChangeEvent | null,
  label: "Billing" | "Shipping"
) {
  if (!address?.complete) {
    throw new Error(`${label} address is required.`);
  }
  return address.value;
}

function getShippingAddress(
  catalog: LockerverseCatalog,
  fieldValues: CheckoutFieldValues,
  billingAddress: ReturnType<typeof getCompleteAddress>,
  addresses: CheckoutAddresses
) {
  if (!catalog.widget.fields.shipping) {
    return;
  }
  return fieldValues.shippingMatchesBilling
    ? billingAddress
    : getCompleteAddress(addresses.shipping, "Shipping");
}

function buildConfirmationParams({
  billingAddress,
  catalog,
  email,
  phone,
  shippingAddress,
}: {
  billingAddress: ReturnType<typeof getCompleteAddress>;
  catalog: LockerverseCatalog;
  email: string;
  phone: string;
  shippingAddress: ReturnType<typeof getCompleteAddress> | undefined;
}) {
  const billingDetails = catalog.widget.fields.phone
    ? {
        address: billingAddress.address,
        email,
        name: billingAddress.name,
        phone,
      }
    : {
        address: billingAddress.address,
        email,
        name: billingAddress.name,
      };
  const params = {
    payment_method_data: {
      billing_details: billingDetails,
    },
  };
  if (!shippingAddress) {
    return params;
  }
  return {
    ...params,
    shipping: {
      address: shippingAddress.address,
      name: shippingAddress.name,
      phone: catalog.widget.fields.phone
        ? phone
        : (shippingAddress.phone ?? null),
    },
  };
}

function buildPaymentRequest({
  catalog,
  checkoutItems,
  confirmationToken,
  email,
  fieldValues,
  metadata,
  phone,
  shippingAddress,
}: {
  catalog: LockerverseCatalog;
  checkoutItems: LockerverseCheckoutItem[];
  confirmationToken: string | undefined;
  email: string;
  fieldValues: CheckoutFieldValues;
  metadata: Record<string, string> | undefined;
  phone: string;
  shippingAddress: ReturnType<typeof getCompleteAddress> | undefined;
}) {
  const request: LockerversePaymentSubmission = {
    customFieldAnswers: buildCustomFieldAnswers(
      getCheckoutCustomFields(catalog, checkoutItems),
      fieldValues.customFields
    ),
    email,
  };
  if (confirmationToken) {
    request.confirmationToken = confirmationToken;
  }
  if (metadata) {
    request.metadata = metadata;
  }
  if (catalog.widget.fields.memberId && fieldValues.memberId.trim()) {
    request.memberId = fieldValues.memberId.trim();
  }
  if (catalog.widget.fields.merchSize) {
    request.merchSize = fieldValues.merchSize;
  }
  if (catalog.widget.fields.phone) {
    request.phone = phone;
  }
  if (shippingAddress) {
    const address: LockerverseShipping["address"] = {
      city: shippingAddress.address.city ?? "",
      country: shippingAddress.address.country,
      line1: shippingAddress.address.line1 ?? "",
      postal_code: shippingAddress.address.postal_code ?? "",
      state: shippingAddress.address.state ?? "",
    };
    if (shippingAddress.address.line2) {
      address.line2 = shippingAddress.address.line2;
    }
    request.shipping = { address, name: shippingAddress.name };
  }
  return request;
}

function assertPaymentAttemptActive(isAttemptActive: () => boolean) {
  if (!isAttemptActive()) {
    throw new Error("Payment component is no longer active.");
  }
}

function notifyPaymentRecovery(
  callback: LockerversePaymentProps["onPaymentRecoveryChange"],
  recovery: Parameters<
    NonNullable<LockerversePaymentProps["onPaymentRecoveryChange"]>
  >[0]
) {
  notifyHostCallback(callback, recovery);
}

function mapPaymentAttemptOutcome(
  outcome: PaymentAttemptOutcome,
  quote: LockerversePreparedCheckout,
  email: string,
  failureMessage: string
) {
  if (outcome.kind === "failed") {
    return {
      kind: "payment_failed" as const,
      message: failureMessage,
    };
  }
  if (outcome.kind === "unresolved") {
    return {
      kind: "payment_unresolved" as const,
      paymentReference: outcome.paymentReference,
      reason: outcome.reason,
    };
  }
  return {
    email,
    kind: "payment" as const,
    payment: outcome.payment,
    quote,
  };
}

export type CompletePaymentOptions = {
  addresses: CheckoutAddresses;
  catalog: LockerverseCatalog;
  checkoutItems: LockerverseCheckoutItem[];
  client: LockerverseCheckoutClient;
  elements?: StripeElements | null;
  email: string;
  fieldValues: CheckoutFieldValues;
  isAttemptActive: () => boolean;
  locallyPreviewedTotalCents: number;
  metadata: Record<string, string> | undefined;
  onPaymentRecoveryChange: LockerversePaymentProps["onPaymentRecoveryChange"];
  phone: string;
  quote: LockerverseCheckoutEstimate | LockerversePreparedCheckout;
  stripe?: Stripe | null;
  stripeConnectedAccountId: string | null;
};

export async function completePayment({
  addresses,
  catalog,
  checkoutItems,
  client,
  elements,
  email,
  fieldValues,
  isAttemptActive,
  locallyPreviewedTotalCents,
  metadata,
  onPaymentRecoveryChange,
  phone,
  quote,
  stripe,
  stripeConnectedAccountId,
}: CompletePaymentOptions) {
  const normalizedEmail = email.trim().toLowerCase();
  const normalizedPhone = normalizePhone(phone);
  const parsedTip = getTipCents(quote.subtotalCents, fieldValues);
  if (!parsedTip.success) {
    throw new Error(parsedTip.message);
  }
  const currentQuote = await client.calculateTotal({
    ...buildQuoteRequest(
      catalog,
      fieldValues,
      normalizedEmail,
      parsedTip.cents
    ),
    catalog,
    items: checkoutItems,
  });
  assertPaymentAttemptActive(isAttemptActive);
  if (
    currentQuote.totalCents > 0 &&
    hasMaterialQuoteChange(
      quote,
      currentQuote,
      locallyPreviewedTotalCents,
      stripeConnectedAccountId
    )
  ) {
    return { kind: "quote_changed" as const, quote: currentQuote };
  }

  let completeShippingAddress:
    | ReturnType<typeof getCompleteAddress>
    | undefined;
  let confirmationToken: string | undefined;
  if (currentQuote.totalCents === 0) {
    if (catalog.widget.fields.shipping) {
      const completeBillingAddress = getCompleteAddress(
        addresses.billing,
        "Billing"
      );
      completeShippingAddress = getShippingAddress(
        catalog,
        fieldValues,
        completeBillingAddress,
        addresses
      );
    }
  } else {
    if (!(elements && stripe)) {
      throw new Error("Payment form is still loading.");
    }
    await elements.update({ amount: currentQuote.totalCents });
    assertPaymentAttemptActive(isAttemptActive);
    const elementSubmission = await elements.submit();
    assertPaymentAttemptActive(isAttemptActive);
    if (elementSubmission.error) {
      throw new Error(elementSubmission.error.message);
    }

    const completeBillingAddress = getCompleteAddress(
      addresses.billing,
      "Billing"
    );
    completeShippingAddress = getShippingAddress(
      catalog,
      fieldValues,
      completeBillingAddress,
      addresses
    );
    const tokenResult = await stripe.createConfirmationToken({
      elements,
      params: buildConfirmationParams({
        billingAddress: completeBillingAddress,
        catalog,
        email: normalizedEmail,
        phone: normalizedPhone,
        shippingAddress: completeShippingAddress,
      }),
    });
    assertPaymentAttemptActive(isAttemptActive);
    if (tokenResult.error) {
      throw new Error(tokenResult.error.message);
    }
    confirmationToken = tokenResult.confirmationToken.id;
  }

  notifyPaymentRecovery(onPaymentRecoveryChange, {
    connectedAccountId: currentQuote.payment.connectedAccountId,
    paymentReference: currentQuote.paymentReference,
  });
  const outcome = await submitPaymentAttempt(
    client,
    {
      checkout: currentQuote,
      submission: buildPaymentRequest({
        catalog,
        checkoutItems,
        confirmationToken,
        email: normalizedEmail,
        fieldValues,
        metadata,
        phone: normalizedPhone,
        shippingAddress: completeShippingAddress,
      }),
    },
    stripe
  );
  assertPaymentAttemptActive(isAttemptActive);
  return mapPaymentAttemptOutcome(
    outcome,
    currentQuote,
    normalizedEmail,
    currentQuote.totalCents === 0
      ? "Order could not be completed. Review the details and try again."
      : "Payment was declined. Check your payment details and try again."
  );
}

export type CompletePaymentResult = Awaited<ReturnType<typeof completePayment>>;
